import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, MapPin, Clock, Users, Repeat, ChevronRight, Loader2, Check, Share2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { MetaPixel } from "@/lib/metapixel";
import { useLocationCoords } from "@/hooks/useLocationCoords";
import TopNav from "@/components/TopNav";

const formatRecurrence = (rec) => {
  if (!rec) return "";
  if (rec.freq === "weekly") {
    const days = (rec.days || []).join(", ");
    return days ? `Weekly · ${days}` : "Weekly";
  }
  if (rec.freq === "monthly") return "Monthly";
  if (rec.freq === "custom") return `Every ${rec.interval || 1} days`;
  return "";
};

const formatPrice = (price, currency = "USD") => {
  if (!price || price === 0) return "Free";
  const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
  return `${symbol}${price}`;
};

const SeriesPage = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  // Auto-geocode series without lat/lng so the map iframe still shows.
  const resolvedCoords = useLocationCoords({
    latitude: series?.latitude,
    longitude: series?.longitude,
    address: series?.address,
    location_name: series?.location_name,
    city: series?.city,
  });
  const [loading, setLoading] = useState(true);
  const [myPass, setMyPass] = useState({ has_pass: false });
  const [members, setMembers] = useState({ count: 0, members: [] });
  const [buyingPass, setBuyingPass] = useState(false);

  const fetchSeries = useCallback(async () => {
    setLoading(true);
    try {
      const headers = user ? await authHeaders() : {};
      const res = await fetch(`${API}/series/${seriesId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSeries(data);
        // Fire Meta Pixel ViewContent for the series so Ads Manager can
        // attribute conversions from ad clicks back to this pageview.
        try {
          MetaPixel.viewContent(
            "series",
            data.series_id,
            data.title,
            data.season_pass_current_price || data.season_pass_price || 0,
            data.currency || "GBP"
          );
        } catch (_) { /* noop */ }
      } else {
        toast.error("Series not found");
        navigate("/");
      }
    } catch (e) {
      toast.error("Failed to load series");
    } finally {
      setLoading(false);
    }
  }, [seriesId, user, navigate]);

  const fetchPassAndMembers = useCallback(async () => {
    try {
      const memRes = await fetch(`${API}/series/${seriesId}/members`);
      if (memRes.ok) setMembers(await memRes.json());
    } catch (_) { /* noop */ }
    if (!user) return;
    try {
      const headers = await authHeaders();
      const r = await fetch(`${API}/series/${seriesId}/my-pass-status`, { headers });
      if (r.ok) setMyPass(await r.json());
    } catch (_) { /* noop */ }
  }, [seriesId, user]);

  useEffect(() => { fetchSeries(); }, [fetchSeries]);
  useEffect(() => { fetchPassAndMembers(); }, [fetchPassAndMembers]);

  const handleBuyPass = async () => {
    // Fire InitiateCheckout as soon as the user commits to buying — before we
    // detour to /login on unauthed users. This maximises Meta's attribution.
    if (series) {
      try {
        MetaPixel.initiateCheckout(
          "series",
          series.series_id,
          series.title,
          series.season_pass_current_price || series.season_pass_price || 0,
          series.currency || "GBP"
        );
      } catch (_) { /* noop */ }
    }
    if (!user) {
      navigate("/login", { state: { from: `/series/${seriesId}` } });
      return;
    }
    if (buyingPass) return;
    setBuyingPass(true);
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const ref = new URLSearchParams(window.location.search).get("ref") || "";
      const res = await fetch(`${API}/series/${seriesId}/season-pass/checkout`, {
        method: "POST",
        headers,
        body: JSON.stringify({ origin_url: window.location.origin, ref }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          toast.error("Could not start checkout");
        }
      } else {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        toast.error(detail || "Failed to start checkout");
      }
    } catch (e) {
      toast.error("Checkout error");
    } finally {
      setBuyingPass(false);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/series/${seriesId}${user ? `?ref=${user.user_id}` : ""}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: series?.title || "Join my series on Pouls", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied!");
      }
    } catch (_) { /* user cancelled */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <TopNav />
        <div className="container mx-auto px-4 py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!series) return null;

  const userEmail = (user?.email || "").toLowerCase().trim();
  const hostEmail = (series.host_email || "").toLowerCase().trim();
  const isHost = !!user && (
    (userEmail && hostEmail && userEmail === hostEmail) ||
    (!!series.host_user_id && series.host_user_id === user.user_id)
  );
  const passPrice = series.season_pass_current_price;
  const passEnabled = series.season_pass_enabled && passPrice > 0 && series.sessions_remaining > 0;

  return (
    <div className="min-h-screen bg-background" data-testid="series-page">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={series.image_url} alt={series.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />

        {/* Spots-left pill — mirrors single-event card. Shows spots for the next upcoming session. */}
        {(() => {
          const nextSession = series.upcoming_sessions && series.upcoming_sessions[0];
          if (!nextSession) return null;
          const spotsLeft = Math.max(0, (nextSession.max_players || 0) - (nextSession.current_players || 0));
          const label = spotsLeft === 0 ? "Full" : `${spotsLeft} spot${spotsLeft !== 1 ? "s" : ""} left`;
          const cls = spotsLeft === 0
            ? "bg-muted text-muted-foreground"
            : spotsLeft <= 2
              ? "bg-destructive/90 text-white"
              : "bg-background/90 text-foreground";
          return (
            <div className="absolute top-4 right-4 z-20" data-testid="series-spots-left">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${cls}`}>{label}</span>
            </div>
          );
        })()}

        <div className="relative z-10 container mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-primary text-primary-foreground gap-1">
              <Repeat className="w-3 h-3" /> Series
            </Badge>
            <Badge variant="secondary" className="bg-white/20 text-white border-white/20 capitalize">
              {series.sport}
            </Badge>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-white leading-tight max-w-3xl">
            {series.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-4 text-white/90 text-sm">
            <span className="inline-flex items-center gap-1.5"><Repeat className="w-4 h-4" /> {formatRecurrence(series.recurrence)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {series.time_start} – {series.time_end}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {series.location_name}, {series.city}</span>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="font-display text-2xl mb-2">About this series</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">{series.description || "No description provided."}</p>
            </div>

            <div className="rounded-xl border border-border p-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Sessions</p>
                <p className="font-display text-2xl">{series.total_session_count}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Remaining</p>
                <p className="font-display text-2xl">{series.sessions_remaining}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wider">Per session</p>
                <p className="font-display text-2xl">{formatPrice(series.price_per_session, series.currency)}</p>
              </div>
            </div>

            {/* Location card — same interactive OpenStreetMap treatment as single events. */}
            <div className="p-4 rounded-xl bg-card border border-border" data-testid="series-location-card">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <MapPin className="w-4 h-4 text-primary" /><span>Location</span>
              </div>
              {series.location_name && <p className="font-semibold">{series.location_name}</p>}
              {series.address && <p className="text-sm text-muted-foreground">{series.address}</p>}
              <p className="text-sm mt-1">{series.city}</p>

              {resolvedCoords.hasCoords ? (
                <div className="mt-3 rounded-lg overflow-hidden border border-border" data-testid="series-location-map">
                  <iframe
                    title="Series Location"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${resolvedCoords.longitude - 0.005},${resolvedCoords.latitude - 0.003},${resolvedCoords.longitude + 0.005},${resolvedCoords.latitude + 0.003}&layer=mapnik&marker=${resolvedCoords.latitude},${resolvedCoords.longitude}`}
                    className="w-full h-32"
                    style={{ border: 0 }}
                  />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${resolvedCoords.latitude},${resolvedCoords.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-muted/50 hover:bg-muted px-3 py-2 flex items-center justify-between text-sm transition-colors"
                    data-testid="series-open-in-maps"
                  >
                    <span className="text-muted-foreground">Open in Google Maps</span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                </div>
              ) : resolvedCoords.loading ? (
                <div className="mt-3 rounded-lg border border-border bg-muted/40 px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="series-map-loading">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading map…
                </div>
              ) : (series.location_name || series.address) ? (
                // No coords yet — offer a plain text-search fallback so hosts who
                // created this series before geocoding was wired still get a map link.
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([series.location_name, series.address, series.city].filter(Boolean).join(", "))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 rounded-lg border border-border bg-muted/40 hover:bg-muted px-3 py-2 flex items-center justify-between text-sm transition-colors"
                  data-testid="series-open-in-maps-fallback"
                >
                  <span className="text-muted-foreground">Open in Google Maps</span>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </a>
              ) : null}
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-2xl">Single sessions</h2>
                <span className="text-xs text-muted-foreground">Book one at a time</span>
              </div>

              {/* Mobile-only pass CTA — sits above the single-session list.
                  Duplicated in the right aside for lg+ screens. */}
              <div className="lg:hidden mb-4 space-y-3">
                {myPass.has_pass && (
                  <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4" data-testid="my-pass-banner-mobile">
                    <div className="flex items-center gap-2 text-primary mb-1">
                      <Check className="w-4 h-4" />
                      <p className="text-xs uppercase tracking-wider font-medium">You have a season pass</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Reserve any single session below — no extra payment needed.
                    </p>
                  </div>
                )}
                {!myPass.has_pass && passEnabled && (
                  <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="pass-cta-mobile">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-1">Season pass · whole series</p>
                    <p className="font-display text-3xl mb-1">{formatPrice(passPrice, series.currency)}</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Covers all {series.sessions_remaining} remaining session{series.sessions_remaining !== 1 ? "s" : ""}.
                    </p>
                    <Button
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="buy-pass-btn-mobile"
                      onClick={handleBuyPass}
                      disabled={buyingPass}
                    >
                      {buyingPass ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : "Buy season pass"}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Or book single sessions below for {formatPrice(series.price_per_session, series.currency)} each.
                    </p>
                  </div>
                )}
              </div>

              {series.upcoming_sessions.length === 0 ? (
                <p className="text-muted-foreground">No upcoming sessions.</p>
              ) : (
                <div className="space-y-2" data-testid="upcoming-sessions">
                  {series.upcoming_sessions.map((g) => {
                    const d = new Date(g.date);
                    return (
                      <button
                        key={g.game_id}
                        type="button"
                        onClick={() => navigate(`/game/${g.game_id}`)}
                        className="w-full flex items-center justify-between p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors text-left"
                        data-testid={`session-${g.game_id}`}
                      >
                        <div>
                          <p className="font-medium">{d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "short" })}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{g.time_start} – {g.time_end} · {g.current_players}/{g.max_players} players</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{formatPrice(g.price, g.currency)}</span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: pass CTA / host actions — pass cards hidden on mobile (they render inline above single-sessions list) */}
          <aside className="space-y-4">
            {/* You-have-a-pass banner */}
            {myPass.has_pass && (
              <div className="hidden lg:block rounded-2xl border border-primary/30 bg-primary/10 p-5" data-testid="my-pass-banner">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <Check className="w-4 h-4" />
                  <p className="text-xs uppercase tracking-wider font-medium">You have a season pass</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Reserve any session below — no extra payment needed.
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-3 gap-2"
                  onClick={handleShare}
                  data-testid="share-series-btn"
                >
                  <Share2 className="w-4 h-4" /> Invite friends
                </Button>
              </div>
            )}

            {/* Pass purchase CTA (only if user doesn't already have a pass) — desktop-only; mobile version is inline above the single-sessions list. */}
            {!myPass.has_pass && passEnabled && (
              <div className="hidden lg:block rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="pass-cta">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Season pass · whole series</p>
                <p className="font-display text-3xl mb-1">{formatPrice(passPrice, series.currency)}</p>
                <p className="text-xs text-muted-foreground mb-4">
                  Covers all {series.sessions_remaining} remaining session{series.sessions_remaining !== 1 ? "s" : ""}.
                  {series.sessions_remaining < series.total_session_count && (
                    <span> Pro-rated from full price {formatPrice(series.season_pass_price, series.currency)}.</span>
                  )}
                </p>
                <Button
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="buy-pass-btn"
                  onClick={handleBuyPass}
                  disabled={buyingPass}
                >
                  {buyingPass ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : "Buy season pass"}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Or book single sessions below for {formatPrice(series.price_per_session, series.currency)} each.
                </p>
              </div>
            )}

            {/* Share for everyone (when no pass CTA above) */}
            {!myPass.has_pass && !passEnabled && (
              <Button variant="outline" className="w-full gap-2" onClick={handleShare} data-testid="share-series-btn">
                <Share2 className="w-4 h-4" /> Share this series
              </Button>
            )}

            {/* Members */}
            {members.count > 0 && (
              <div className="rounded-2xl border border-border p-5" data-testid="members-card">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">
                  {members.count} pass holder{members.count !== 1 ? "s" : ""}
                </p>
                <div className="flex -space-x-2">
                  {members.members.slice(0, 6).map(m => (
                    <img
                      key={m.user_id}
                      src={m.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=random`}
                      alt={m.name}
                      title={m.name}
                      className="w-9 h-9 rounded-full ring-2 ring-card object-cover"
                      onClick={() => navigate(`/profile/${m.user_id}`)}
                    />
                  ))}
                  {members.count > 6 && (
                    <div className="w-9 h-9 rounded-full bg-muted text-xs flex items-center justify-center ring-2 ring-card">
                      +{members.count - 6}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-border p-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">Hosted by</p>
              <div className="flex items-center gap-3">
                {series.host_image_url && (
                  <img src={series.host_image_url} alt={series.host_name} className="w-10 h-10 rounded-full object-cover" />
                )}
                <p className="font-medium">{series.host_name}</p>
              </div>
            </div>

            {isHost && (
              <div className="rounded-2xl border border-border p-5 space-y-2">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Host tools</p>
                <Button variant="outline" className="w-full" onClick={() => toast.info("Series editing — coming soon (Chunk 3.5)")}>Edit series</Button>
                <Button
                  variant="outline"
                  className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={async () => {
                    if (!window.confirm("Cancel this series and all future sessions? Pass holders will be credited pro-rata.")) return;
                    const headers = await authHeaders();
                    const res = await fetch(`${API}/series/${series.series_id}/cancel`, { method: "POST", headers });
                    if (res.ok) {
                      const data = await res.json();
                      toast.success(`Series cancelled. ${data.pass_holders_refunded || 0} pass holder(s) credited.`);
                      fetchSeries();
                      fetchPassAndMembers();
                    } else {
                      toast.error("Failed to cancel series");
                    }
                  }}
                  data-testid="cancel-series-btn"
                >
                  Cancel series
                </Button>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default SeriesPage;
